import type {
	ICampaignWizardForm,
	ISessionFormApiPayload,
	IAdGroup,
	IAppAd,
	IConversionAction,
	IConversionCatalogItem,
} from "../../../../../../../../../shared/types/form.type";

/** Рядки resource_name з контексту форми → об'єкти для campaign_form_raw (контракт WS). */
export function conversionResourceNamesToRawActions(
	keys: string[],
): IConversionAction[] {
	const empty: Omit<IConversionAction, "resource_name"> = {
		app_id: "",
		name: "",
		status: "",
		type: "",
		category: "",
	};
	return keys.map((resource_name) => ({ ...empty, resource_name }));
}

/** Канонічна форма goal name для порівняння: "FIRST_OPEN" / "first_open" / "First Open" → "firstopen". */
export function normalizeConversionGoalToken(value: string): string {
	return value.trim().toLowerCase().replace(/[^a-z0-9]/g, "");
}

export interface ConversionGoalResolution {
	/** Узгоджені ключі (resource names) для поля selective_optimization_conversion_actions. */
	keys: string[];
	/** Виконані підстановки семантичних goal names чернетки на resource names каталогу. */
	substitutions: Array<{ from: string; to: string }>;
}

/**
 * Узгоджує ключі selective_optimization_conversion_actions з каталогом конверсій.
 *
 * Чернетка basic-режиму (HIS) несе семантичні goal names ("first_open",
 * "in_app_purchase"), а ключі каталогу — Google Ads resource names. Валідні
 * resource names проходять як є; goal name підставляється на єдиний
 * збіг за нормалізованими category/type/name каталогу; ключі без однозначного
 * збігу відкидаються (щоб не відправити сміття в Google Ads). Порядок
 * зберігається, дублікати (у т.ч. після підстановки) прибираються.
 */
export function resolveConversionKeysAgainstCatalog(
	current: string[],
	catalog: IConversionCatalogItem[],
): ConversionGoalResolution {
	const validKeys = new Set(catalog.map((c) => c.key));

	// Нормалізований токен → безліч resource names каталогу (кілька = неоднозначно).
	const lookup = new Map<string, Set<string>>();
	for (const item of catalog) {
		const candidates = [item.category, item.type, item.name];
		for (const candidate of candidates) {
			if (typeof candidate !== "string" || candidate.trim() === "") continue;
			const token = normalizeConversionGoalToken(candidate);
			if (!token) continue;
			const targets = lookup.get(token) ?? new Set<string>();
			targets.add(item.key);
			lookup.set(token, targets);
		}
	}

	const seen = new Set<string>();
	const keys: string[] = [];
	const substitutions: Array<{ from: string; to: string }> = [];
	for (const raw of current) {
		const key = typeof raw === "string" ? raw : "";
		let resolved: string = key;
		if (!validKeys.has(key)) {
			const token = normalizeConversionGoalToken(key);
			const targets = token ? lookup.get(token) : undefined;
			resolved = targets && targets.size === 1 ? [...targets][0] : "";
		}
		if (resolved === "" || seen.has(resolved)) continue;
		if (resolved !== key) substitutions.push({ from: key, to: resolved });
		seen.add(resolved);
		keys.push(resolved);
	}
	return { keys, substitutions };
}

/** Одна порожня ad group для мінімального стану форми (завжди ≥1 група в UI). */
export function createDefaultAdGroup(): IAdGroup {
	return {
		name: "",
		appads: [{ headlines: [], descriptions: [] }],
	};
}

/** Порожня форма кампанії перед заповненням з API / ручним вводом. */
export function createEmptyCampaignWizardForm(
	userId: string,
): ICampaignWizardForm {
	return {
		type: "",
		action: "",
		user_id: userId,
		campaign_form_raw: {
			budget_amount: "",
			campaign_name: "",
			campaign_status: "",
			app_store: "",
			app_id: "",
			target_cpa_micros: "",
			selective_optimization_conversion_actions: [],
			geo_targets: [],
			language_targets: [],
			start_date: "",
			end_date: "",
		},
		campaign_context: {
			customer_id: "",
			account_name: "",
			budget: { amount_micros: "" },
			campaign: {
				name: "",
				status: "",
				app_store: "",
				app_id: "",
				target_cpa_micros: "",
				selective_optimization_conversion_actions: [],
				geo_targets: [],
				language_targets: [],
			},
			adgroups: [createDefaultAdGroup()],
		},
	};
}

/** Приводить значення з API до string[] (один рядок, числа, змішані масиви). */
export function coerceStringArray(value: unknown): string[] {
	if (value == null) return [];
	if (Array.isArray(value)) {
		return value.flatMap((item) => {
			if (typeof item === "string") return item.length > 0 ? [item] : [];
			if (typeof item === "number" && Number.isFinite(item))
				return [String(item)];
			return [];
		});
	}
	if (typeof value === "string") return value.length > 0 ? [value] : [];
	return [];
}

function normalizeAdGroups(groups: IAdGroup[]): IAdGroup[] {
	return groups.map((ag) => {
		const fromAg = ag as unknown as {
			appads?: IAppAd[];
			appAds?: IAppAd[];
		};
		const rawAppads = fromAg.appads ?? fromAg.appAds;
		const appads =
			Array.isArray(rawAppads) && rawAppads.length > 0
				? rawAppads.map((a) => ({
						headlines: coerceStringArray(a?.headlines),
						descriptions: coerceStringArray(a?.descriptions),
					}))
				: [{ headlines: [] as string[], descriptions: [] as string[] }];
		return {
			name: typeof ag.name === "string" ? ag.name : "",
			appads,
		};
	});
}

/** Накладає знімок GET /session/:id/form на поточну форму (іммутабельно). */
export function mergeSessionPayloadIntoCampaignForm(
	base: ICampaignWizardForm,
	payload: ISessionFormApiPayload,
): ICampaignWizardForm {
	const next: ICampaignWizardForm = structuredClone(base);
	const ctx = next.campaign_context;
	const p = payload;

	if (p.budget?.amount_micros !== undefined) {
		ctx.budget.amount_micros = p.budget.amount_micros;
		next.campaign_form_raw.budget_amount = p.budget.amount_micros;
	}
	if (p.customer_id !== undefined && p.customer_id !== "") {
		ctx.customer_id = p.customer_id.replace(/\D/g, "");
	}
	if (p.account_name !== undefined) {
		ctx.account_name = p.account_name;
	}
	if (Array.isArray(p.adgroups)) {
		const normalized = normalizeAdGroups(p.adgroups);
		ctx.adgroups =
			normalized.length > 0 ? normalized : [createDefaultAdGroup()];
	}

	const pc = p.campaign;
	if (pc) {
		const c = ctx.campaign;
		const raw = next.campaign_form_raw;
		if (pc.name !== undefined) {
			c.name = pc.name;
			raw.campaign_name = pc.name;
		}
		if (pc.status !== undefined) {
			c.status = pc.status;
			raw.campaign_status = pc.status;
		}
		if (pc.app_store !== undefined) {
			c.app_store = pc.app_store;
			raw.app_store = pc.app_store;
		}
		if (pc.app_id !== undefined) {
			c.app_id = pc.app_id;
			raw.app_id = pc.app_id;
		}
		if (pc.target_cpa_micros !== undefined) {
			c.target_cpa_micros = pc.target_cpa_micros;
			raw.target_cpa_micros = pc.target_cpa_micros;
		}
		if (pc.selective_optimization_conversion_actions !== undefined) {
			const keys = [...pc.selective_optimization_conversion_actions];
			c.selective_optimization_conversion_actions = keys;
			raw.selective_optimization_conversion_actions =
				conversionResourceNamesToRawActions(keys);
		}
		if (pc.start_date !== undefined) {
			raw.start_date = pc.start_date;
		}
		if (pc.end_date !== undefined) {
			raw.end_date = pc.end_date;
		}
		if (pc.geo_targets !== undefined) {
			c.geo_targets = [...pc.geo_targets];
			raw.geo_targets = [...pc.geo_targets];
		}
		if (pc.language_targets !== undefined) {
			c.language_targets = [...pc.language_targets];
			raw.language_targets = [...pc.language_targets];
		}
	}

	if (p.start_date !== undefined && p.start_date !== "") {
		next.campaign_form_raw.start_date = p.start_date;
	}
	if (p.end_date !== undefined && p.end_date !== "") {
		next.campaign_form_raw.end_date = p.end_date;
	}

	if (typeof p.type === "string") {
		next.type = p.type;
	}
	if (typeof p.action === "string") {
		next.action = p.action;
	}

	return next;
}

/** Гарантує узгодженість raw з campaign_context перед відправкою на WS. */
export function alignCampaignFormRawFromContext(form: ICampaignWizardForm) {
	const raw = form.campaign_form_raw;
	const ctx = form.campaign_context;
	const c = ctx.campaign;
	return {
		...form,
		campaign_form_raw: {
			...raw,
			budget_amount: ctx.budget.amount_micros,
			campaign_name: c.name,
			campaign_status: c.status,
			app_store: c.app_store,
			app_id: c.app_id,
			target_cpa_micros: c.target_cpa_micros,
			selective_optimization_conversion_actions:
				conversionResourceNamesToRawActions(
					c.selective_optimization_conversion_actions,
				),
			geo_targets: [...c.geo_targets],
			language_targets: [...c.language_targets],
		},
	};
}

export function formatCustomerIdToAccountId(customerId: string): string {
	const d = customerId.replace(/\D/g, "");
	if (d.length === 10) return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
	return customerId;
}
